/**
 * Phase 17 Plan 17-02 — KPI wire types mirroring the 17-01 backend DTOs.
 *
 * KpiUnit is a string-literal union (not an enum) for stability on the wire.
 * The backend serializes KpiUnit via JsonStringEnumConverter.
 */

/** KPI unit options matching the backend KpiUnit enum (string-serialized). */
export type KpiUnit =
  | 'Nos'
  | 'Meters'
  | 'RunningMeters'
  | 'Hours'
  | 'Kilometers'
  | 'Liters'
  | 'Kilograms'
  | 'Units'
  | 'Trips'
  | 'Custom';

/** All valid KpiUnit values — useful for dropdowns. */
export const KPI_UNITS: readonly KpiUnit[] = [
  'Nos',
  'Meters',
  'RunningMeters',
  'Hours',
  'Kilometers',
  'Liters',
  'Kilograms',
  'Units',
  'Trips',
  'Custom',
];

/** Human-readable labels for each KpiUnit. */
export const KPI_UNIT_LABELS: Readonly<Record<KpiUnit, string>> = {
  Nos: 'Nos',
  Meters: 'Meters',
  RunningMeters: 'Running Meters',
  Hours: 'Hours',
  Kilometers: 'Kilometers',
  Liters: 'Liters',
  Kilograms: 'Kilograms',
  Units: 'Units',
  Trips: 'Trips',
  Custom: 'Custom',
};

/** A machine linked to a KPI — embedded in KpiDetail. */
export interface KpiApplicableMachine {
  readonly machineId: number;
  readonly machineCode: string;
  readonly machineName: string;
  /** Denormalized `vehicle_number` custom field — shown as `Name (Vehicle Number)`. Optional on the wire. */
  readonly vehicleNumber?: string | null;
}

/** A KPI row in the paginated list (`GET /api/workorders/:woId/kpis`). */
export interface KpiListItem {
  readonly id: number;
  readonly workOrderId: number;
  readonly name: string;
  readonly unit: KpiUnit;
  readonly customUnitText: string | null;
  readonly description: string | null;
  readonly targetPerShift: number | null;
  readonly applicableMachineCount: number;
  readonly isActive: boolean;
  /**
   * Phase 23 (Plan 23-01 D-239) — Pinned-to-dashboard flag. Drives the star
   * button in WorkOrderDetailPage's KPI list and the "Pinned KPIs" rollup
   * widget. Server-enforced max 3 pinned per WO; toggled via
   * `togglePinKpiRequest` (Phase 23 Plan 23-03). Optional on the wire for
   * backward compatibility — pre-Phase-23 fixtures may omit it.
   */
  readonly isPinnedToDashboard?: boolean;
}

/** Full KPI detail including linked machines and entry count (`GET /api/workorders/:woId/kpis/:id`). */
export interface KpiDetail extends KpiListItem {
  /** Flat ID list — the canonical shape returned by the backend's KpiDetailDto. */
  readonly applicableMachineIds?: readonly number[];
  /** Optional enriched objects (id + code + name). May be omitted by the backend;
   * EditKpiDialog falls back to applicableMachineIds. */
  readonly applicableMachines?: readonly KpiApplicableMachine[];
  /** Number of logsheet entries that have captured a value for this KPI.
   * Used by EditKpiDialog to disable the Unit field when > 0. Optional because
   * the current backend KpiDetailDto does not yet include it; treat undefined as 0. */
  readonly entryCount?: number;
}

/** Body for `POST /api/workorders/:woId/kpis`. */
export interface CreateKpiBody {
  readonly name: string;
  readonly unit: KpiUnit;
  readonly customUnitText?: string;
  readonly description?: string;
  readonly targetPerShift?: number;
  readonly applicableMachineIds?: readonly number[];
}

/** Body for `PUT /api/workorders/:woId/kpis/:id`. Same fields as Create. */
export type UpdateKpiBody = CreateKpiBody;

/** Response from `POST /api/workorders/:woId/kpis/:id/deactivate`. */
export interface DeactivateKpiResponse {
  readonly id: number;
  readonly isActive: false;
  readonly entryCount: number;
}
